import React from 'react';
import { View, Text, Pressable, StyleSheet, Dimensions } from 'react-native';
import { DrawerActions, CommonActions } from '@react-navigation/native';
import { useTheme } from 'react-native-paper';
import type { NativeStackHeaderProps } from '@react-navigation/native-stack';
import { Menu as MenuIcon, ArrowLeft } from 'lucide-react-native';
import { useMobileNavigation } from '../../hooks/useMobileNavigation';
import { shouldShowBackButton } from '../../config/navigationConfig';

interface ResponsiveHeaderProps extends NativeStackHeaderProps {
  showSidebar?: boolean;
  sidebarContent?: React.ReactNode;
  onNavigateHeader?: (route: string) => void;
  forceShowBack?: boolean;
  customBackAction?: () => void;
}

/**
 * ResponsiveHeader - Adaptive header that changes layout based on screen size
 *
 * Features:
 * - Compact swappable header for mobile (< 768px)
 * - Full-size header with sidebar for desktop (≥ 768px)
 * - Maintains navigation functionality across all screen sizes
 * - Responsive to orientation changes
 * - Safe area handled by parent SafeAreaView in BaseLayoutNavigator
 */
export function ResponsiveHeader({
  navigation,
  options,
  back,
  showSidebar = false,
  sidebarContent,
  onNavigateHeader,
  forceShowBack = false,
  customBackAction,
}: ResponsiveHeaderProps) {
  const theme = useTheme();
  const [isLargeScreen, setIsLargeScreen] = React.useState(false);

  // Smart navigation logic
  const { showBackButton: hookShowBackButton, backAction } =
    useMobileNavigation({
      forceShowBack,
      ...(customBackAction && { customBackAction }),
    });

  // Get current route name from navigation state
  const navState = (navigation as any).getState?.();
  const routeName = navState?.routes?.[navState?.index]?.name || '';
  const isIndexRoute = routeName === 'index';

  // Check if this route should show back button based on config
  const configShowBackButton = shouldShowBackButton(routeName);

  const isInDrawerDetailRoute = React.useMemo(() => {
    let parent: any = (navigation as any).getParent?.();
    let depth = 0;
    while (parent && depth < 5) {
      const state: any = parent.getState?.();
      const currentRoute = state?.routes?.[state?.index];
      if (
        currentRoute?.name === 'journey' ||
        currentRoute?.name === 'aiExperience'
      ) {
        return true;
      }
      parent = parent.getParent?.();
      depth += 1;
    }
    return false;
  }, [navigation]);

  // Determine if back button should be shown
  // For large screens: use config-based logic (child routes show back)
  // For small screens: use hook logic with drawer consideration
  const showBackButton = React.useMemo(() => {
    if (isLargeScreen) {
      // Large screens: show back button for child routes only
      // forceShowBack can override this
      if (forceShowBack !== undefined) {
        return forceShowBack;
      }
      return configShowBackButton;
    } else {
      // Small screens: use existing hook logic
      if (forceShowBack !== undefined) {
        return forceShowBack;
      }
      return hookShowBackButton && (!isIndexRoute || isInDrawerDetailRoute);
    }
  }, [
    isLargeScreen,
    forceShowBack,
    configShowBackButton,
    hookShowBackButton,
    isIndexRoute,
    isInDrawerDetailRoute,
  ]);

  const titleText = typeof options.title === 'string' ? options.title : '';
  const headerTintColor =
    (options.headerTintColor as string | undefined) ?? theme.colors.onSurface;
  const headerBg =
    (options.headerStyle as any)?.backgroundColor ?? theme.colors.surface;
  const isTransparent = Boolean(options.headerTransparent);
  const showShadow = options.headerShadowVisible !== false;
  const titleAlign =
    (options.headerTitleAlign as 'left' | 'center' | undefined) ?? 'center';

  React.useEffect(() => {
    const updateScreenSize = () => {
      const { width } = Dimensions.get('window');
      setIsLargeScreen(width > 768);
    };

    updateScreenSize();

    const subscription = Dimensions.addEventListener(
      'change',
      updateScreenSize
    );
    return () => subscription?.remove();
  }, []);

  const renderTitle = () => {
    if (typeof options.headerTitle === 'function') {
      return options.headerTitle({
        children: titleText,
        tintColor: headerTintColor,
      });
    }
    if (typeof options.headerTitle === 'string') {
      return (
        <Text
          numberOfLines={1}
          style={[styles.title, { color: theme.colors.onSurface }]}
        >
          {options.headerTitle}
        </Text>
      );
    }
    return (
      <Text
        numberOfLines={1}
        style={[
          styles.title,
          { color: headerTintColor },
          options.headerTitleStyle as any,
        ]}
      >
        {titleText}
      </Text>
    );
  };

  const canGoBack = Boolean(back);

  const hasDrawerParent = React.useMemo(() => {
    // Check if current navigator is a drawer
    const currentState: any = (navigation as any).getState?.();
    if (currentState?.type === 'drawer') return true;

    // Check parent navigators for drawer
    let parent: any = (navigation as any).getParent?.();
    let depth = 0;
    while (parent && depth < 5) {
      const state: any = parent.getState?.();
      if (state?.type === 'drawer') return true;
      parent = parent.getParent?.();
      depth += 1;
    }
    return false;
  }, [navigation]);

  const renderMobileHeader = () => (
    <View
      style={[
        styles.container,
        {
          backgroundColor: isTransparent ? 'transparent' : headerBg,
          borderBottomColor:
            theme.colors.outlineVariant ?? theme.colors.outline,
        },
      ]}
    >
      <View
        style={[
          styles.inner,
          {
            borderBottomWidth: showShadow ? StyleSheet.hairlineWidth : 0,
            borderBottomColor:
              theme.colors.outlineVariant ?? theme.colors.outline,
          },
        ]}
      >
        <View style={styles.left}>
          {options.headerLeft ? (
            options.headerLeft({ tintColor: headerTintColor, canGoBack })
          ) : hasDrawerParent && !showBackButton ? (
            <Pressable
              onPress={() => {
                navigation.dispatch(DrawerActions.openDrawer());
              }}
              accessibilityRole='button'
              accessibilityLabel='Open menu'
              hitSlop={12}
              style={styles.backButton}
              testID='responsive-header-menu'
            >
              <MenuIcon color={headerTintColor} size={22} />
            </Pressable>
          ) : showBackButton ? (
            <Pressable
              onPress={backAction}
              accessibilityRole='button'
              accessibilityLabel='Go back'
              hitSlop={12}
              style={styles.backButton}
              testID='responsive-header-back'
            >
              <ArrowLeft color={headerTintColor} size={22} />
            </Pressable>
          ) : null}
        </View>
        <View
          style={[
            styles.center,
            titleAlign === 'left' && {
              alignItems: 'flex-start',
              paddingLeft: 8,
            },
          ]}
        >
          {renderTitle()}
        </View>
        <View style={styles.right}>
          {options.headerRight?.({ tintColor: headerTintColor, canGoBack }) ??
            null}
        </View>
      </View>
    </View>
  );

  const DesktopNav = () => {
    const items = [
      { id: 'recipes', label: 'Recipes' },
      { id: 'creators', label: 'Creators' },
      { id: 'equipment', label: 'Equipment' },
      { id: 'profile', label: 'Profile' },
    ];

    return (
      <View style={styles.navContainer}>
        {items.map(item => (
          <Pressable
            key={item.id}
            onPress={() => {
              if (onNavigateHeader) {
                onNavigateHeader(item.id);
                return;
              }
              // Use reset to ensure we navigate to the index screen of the stack
              // This fixes the issue where clicking on a nav item preserves the detail screen
              navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'home',
                      state: {
                        routes: [
                          {
                            name: item.id,
                            state: {
                              routes: [{ name: 'index' }],
                              index: 0,
                            },
                          },
                        ],
                        index: 0,
                      },
                    },
                  ],
                })
              );
            }}
            accessibilityRole='button'
            accessibilityLabel={`Navigate to ${item.label}`}
            style={styles.navItem}
            hitSlop={8}
          >
            <Text style={[styles.navText, { color: theme.colors.onSurface }]}>
              {item.label}
            </Text>
          </Pressable>
        ))}
      </View>
    );
  };

  const renderDesktopHeader = () => (
    <View style={styles.desktopContainer}>
      {showSidebar && sidebarContent && (
        <View style={[styles.sidebar, { backgroundColor: headerBg }]}>
          {sidebarContent}
        </View>
      )}

      <View style={styles.desktopContent}>
        <View
          style={[
            styles.desktopHeader,
            {
              backgroundColor: isTransparent ? 'transparent' : headerBg,
              borderBottomColor:
                theme.colors.outlineVariant ?? theme.colors.outline,
            },
          ]}
        >
          <View
            style={[
              styles.desktopInner,
              {
                borderBottomWidth: showShadow ? StyleSheet.hairlineWidth : 0,
                borderBottomColor:
                  theme.colors.outlineVariant ?? theme.colors.outline,
              },
            ]}
          >
            <View style={styles.desktopLeft}>
              {options.headerLeft ? (
                options.headerLeft({ tintColor: headerTintColor, canGoBack })
              ) : showBackButton ? (
                <Pressable
                  onPress={backAction}
                  accessibilityRole='button'
                  accessibilityLabel='Go back'
                  hitSlop={12}
                  style={styles.backButton}
                  testID='responsive-header-back-desktop'
                >
                  <ArrowLeft color={headerTintColor} size={22} />
                </Pressable>
              ) : null}
            </View>

            <View style={styles.desktopCenter}>
              {/* Always show navigation menu on desktop for familiar web experience */}
              <DesktopNav />
            </View>

            <View style={styles.desktopRight}>
              {options.headerRight?.({
                tintColor: headerTintColor,
                canGoBack,
              }) ?? null}
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  return isLargeScreen ? renderDesktopHeader() : renderMobileHeader();
}

const styles = StyleSheet.create({
  // Mobile styles
  container: {},
  inner: {
    minHeight: 56,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  left: {
    width: 48,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  backButton: {
    padding: 8,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backText: {
    marginLeft: 4,
    fontSize: 16,
    fontWeight: '500',
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
  },
  right: {
    width: 48,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },

  // Desktop styles
  desktopContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  sidebar: {
    width: 280,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: '#e0e0e0',
  },
  desktopContent: {
    flex: 1,
  },
  desktopHeader: {},
  desktopInner: {
    minHeight: 64,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  desktopLeft: {
    minWidth: 48,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  desktopCenter: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 0,
  },
  desktopRight: {
    minWidth: 48,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  navContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  navItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginRight: 8,
  },
  navText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ResponsiveHeader;
